import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

const WORDS = ['BOOP!', 'BOOP!', 'BOOP!', 'Booped!', 'Snoot booped!'];

// Global boop counter API (Cloudflare Worker). Override with REACT_APP_BOOP_API.
const API = (process.env.REACT_APP_BOOP_API || 'https://boop-counter.boopthecoin.workers.dev').replace(/\/$/, '');
const FLUSH_MS = 4000;      // send batched taps at most every 4s
const MAX_BATCH = 30;       // server clamps to 30 per request anyway
const STATS_MS = 15000;     // refresh global stats every 15s

const flag = (code) =>
  /^[A-Z]{2}$/.test(code) && code !== 'XX'
    ? String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : '🌐';

const countryName = (code) => {
  try {
    if (code === 'XX') return 'Unknown';
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch (e) {
    return code;
  }
};

const fmt = (n) => Number(n || 0).toLocaleString('en-US');

const TapToBoop = () => {
  const [count, setCount] = useState(0); // local boops this visit
  const [stats, setStats] = useState(null); // {total, countries}
  const [pressed, setPressed] = useState(false);
  const [bursts, setBursts] = useState([]);
  const zoneRef = useRef(null);
  const nextId = useRef(0);
  const pending = useRef(0);   // taps not yet sent
  const inFlight = useRef(0);  // taps sent but not yet reflected in stats
  const lastSend = useRef(0);
  const timer = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/stats`);
      if (!r.ok) throw new Error(String(r.status));
      const d = await r.json();
      if (typeof d.total === 'number') {
        inFlight.current = 0;
        setStats(d);
      }
    } catch (e) {
      /* offline / API down: keep local counter */
    }
  }, []);

  const flush = useCallback(async () => {
    timer.current = null;
    const n = Math.min(pending.current, MAX_BATCH);
    if (n <= 0) return;
    pending.current -= n;
    lastSend.current = Date.now();
    try {
      const r = await fetch(`${API}/boop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: n }),
      });
      if (r.ok) inFlight.current += (await r.json()).accepted || 0;
    } catch (e) {
      /* ignore: local count still shows */
    }
    if (pending.current > 0 && !timer.current) timer.current = window.setTimeout(flush, FLUSH_MS);
  }, []);

  const schedule = useCallback(() => {
    if (timer.current) return;
    const wait = Math.max(0, FLUSH_MS - (Date.now() - lastSend.current));
    timer.current = window.setTimeout(flush, wait);
  }, [flush]);

  useEffect(() => {
    loadStats();
    const iv = window.setInterval(loadStats, STATS_MS);
    const onHide = () => {
      const n = Math.min(pending.current, MAX_BATCH);
      if (n > 0 && navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/boop`, JSON.stringify({ count: n }));
        pending.current -= n;
      }
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.clearInterval(iv);
      window.removeEventListener('pagehide', onHide);
      if (timer.current) window.clearTimeout(timer.current);
      onHide();
    };
  }, [loadStats]);

  const handleBoop = (ev) => {
    setCount((c) => c + 1);
    pending.current += 1;
    schedule();
    setPressed(true);
    window.setTimeout(() => setPressed(false), 130);

    const zone = zoneRef.current;
    if (!zone) return;
    const zr = zone.getBoundingClientRect();
    const keyboard = ev.clientX === 0 && ev.clientY === 0;
    const x = keyboard ? zr.width / 2 - 40 : ev.clientX - zr.left - 40 + (Math.random() * 40 - 20);
    const y = keyboard ? 30 : ev.clientY - zr.top - 40;
    const id = nextId.current++;
    const word = WORDS[Math.floor(Math.random() * WORDS.length)];
    setBursts((b) => [...b, { id, x, y, word }]);
    window.setTimeout(() => setBursts((b) => b.filter((item) => item.id !== id)), 950);
  };

  // Optimistic: server total + taps not yet reflected in stats.
  const shown = stats ? stats.total + pending.current + inFlight.current : count;
  const top = stats ? stats.countries.slice(0, 5) : [];

  return (
    <div className="mx-auto mt-6 max-w-[420px] text-center lg:mt-0 lg:max-w-[500px]">
      <div ref={zoneRef} className="relative">
        <button
          type="button"
          onClick={handleBoop}
          aria-label="Boop the nose"
          className={cn(
            'relative block w-full cursor-pointer overflow-hidden rounded-[28px] border-4 border-ink bg-white p-0 shadow-hard-lg transition-transform duration-100',
            pressed && 'scale-[0.96]'
          )}
        >
          <img src="/assets/boop-nose.jpg" alt="A fluffy dog nose waiting to be booped" className="pointer-events-none h-auto w-full" width={900} height={600} />
          <span className="pointer-events-none absolute inset-x-0 bottom-2.5 text-center">
            <span className="inline-block rounded-full bg-ink px-[18px] py-1.5 text-[15px] font-bold tracking-[1px] text-sun">TAP TO BOOP</span>
          </span>
        </button>
        {bursts.map((b) => (
          <span key={b.id} className="burst" style={{ left: b.x, top: b.y }} aria-hidden="true">
            {b.word}
          </span>
        ))}
      </div>
      <p className="mt-3 text-base font-semibold" aria-live="polite">
        {stats ? 'Boops delivered worldwide' : 'Boops delivered'}:{' '}
        <b className="rounded-[10px] border-2 border-ink bg-sun px-2.5 py-px tabular-nums">{fmt(shown)}</b>
      </p>
      {stats && count > 0 && <p className="mt-1 text-sm opacity-70">You booped {fmt(count)} times this visit</p>}
      {top.length > 0 && (
        <div className="mx-auto mt-3 rounded-[18px] border-2 border-ink bg-white px-4 py-2.5 text-left shadow-hard">
          <p className="mb-1.5 text-center text-sm font-bold uppercase tracking-[1px]">Top boopers</p>
          <ol className="flex flex-wrap justify-center gap-1.5">
            {top.map((c, i) => (
              <li
                key={c.code}
                title={countryName(c.code)}
                className={cn('rounded-full border-2 border-ink px-2.5 py-0.5 text-sm font-semibold tabular-nums', i === 0 ? 'bg-sun' : 'bg-white')}
              >
                <span aria-hidden="true">{flag(c.code)}</span> {countryName(c.code)}{' '}
                <span className="opacity-70">{fmt(c.count)}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default TapToBoop;
